"use client";

import { useEffect, useRef, useState } from "react";
import { RefreshCw, X } from "lucide-react";

const RELEASE_CHECK_INTERVAL_MS = 60_000;

type AvailableRelease = {
  releaseId: string;
  label: string;
};

function readAvailableRelease(value: unknown): AvailableRelease | null {
  if (!value || typeof value !== "object") return null;

  const releaseId = Reflect.get(value, "releaseId");
  const label = Reflect.get(value, "label");
  if (
    typeof releaseId !== "string" ||
    !releaseId ||
    releaseId.length > 100 ||
    typeof label !== "string" ||
    !label ||
    label.length > 100
  ) {
    return null;
  }

  return { releaseId, label };
}

export function ReleaseMonitor({
  currentReleaseId,
}: {
  currentReleaseId: string;
}) {
  const [availableRelease, setAvailableRelease] =
    useState<AvailableRelease | null>(null);
  const dismissedReleaseId = useRef<string | null>(null);

  useEffect(() => {
    let stopped = false;
    let requestController: AbortController | null = null;

    async function checkForRelease() {
      requestController?.abort();
      requestController = new AbortController();

      try {
        const response = await fetch(`/api/version?t=${Date.now()}`, {
          cache: "no-store",
          headers: { accept: "application/json" },
          signal: requestController.signal,
        });
        if (!response.ok || stopped) return;

        const release = readAvailableRelease(await response.json());
        if (
          release &&
          release.releaseId !== currentReleaseId &&
          release.releaseId !== dismissedReleaseId.current
        ) {
          setAvailableRelease(release);
        }
      } catch {
        // Version checks are best-effort and must never interrupt panel use.
      }
    }

    const interval = window.setInterval(
      checkForRelease,
      RELEASE_CHECK_INTERVAL_MS,
    );
    const checkWhenVisible = () => {
      if (document.visibilityState === "visible") void checkForRelease();
    };

    window.addEventListener("focus", checkForRelease);
    document.addEventListener("visibilitychange", checkWhenVisible);

    return () => {
      stopped = true;
      requestController?.abort();
      window.clearInterval(interval);
      window.removeEventListener("focus", checkForRelease);
      document.removeEventListener("visibilitychange", checkWhenVisible);
    };
  }, [currentReleaseId]);

  if (!availableRelease) return null;

  function dismiss() {
    dismissedReleaseId.current = availableRelease?.releaseId ?? null;
    setAvailableRelease(null);
  }

  return (
    <aside
      className="release-notice"
      aria-label="Application update available"
    >
      <span className="release-notice-indicator" aria-hidden="true" />
      <span className="release-notice-copy" role="status" aria-live="polite">
        <strong>New version available</strong>
        <small>{availableRelease.label} is ready to load.</small>
      </span>
      <button
        className="release-refresh"
        type="button"
        onClick={() => window.location.reload()}
      >
        <RefreshCw size={14} aria-hidden="true" />
        Refresh
      </button>
      <button
        className="release-dismiss"
        type="button"
        onClick={dismiss}
        aria-label="Dismiss update notice"
      >
        <X size={15} aria-hidden="true" />
      </button>
    </aside>
  );
}
